// QBR narrative generator — calls Claude with the seeded narrative
// prompt to draft the deck's human-readable copy (exec summary,
// per-metric callouts, customer win, MAP, roadmap teaser).
//
// Goes through the standard prompts pipeline:
//   - prompts row with (migration_type='QBR', stage='narrative')
//     supplies the template body. Edit-only via the Prompts admin tab.
//   - promptTemplates.buildSystemPrompt interpolates the
//     {{CUSTOMER_NAME}}, {{METRICS_JSON}}, etc. placeholders.
//   - claudeFetch hits /api/claude (the server-side proxy).
//   - createExtractionRun + updateExtractionRunResult log the call so
//     it shows up in the Runs admin tab alongside PDF / CSV runs and
//     cost tracking works.
//
// Output: a plain JS object matching the prompt's documented shape:
//   {
//     exec_summary: { wins: [...], risks: [...] },
//     metric_callouts: { completion_rate, response_time, ... },
//     customer_win: string,
//     mutual_action_plan: [{ owner: 'fmx'|'customer', item: string }],
//     roadmap_teaser: [string, string, string]
//   }
//
// On failure, throws with a human-readable message. The Preview step
// surfaces it inline.

import { claudeFetch, parseClaudeText } from '../apiClient.js';
import { buildSystemPrompt, extractUsage } from '../promptTemplates.js';
import { getActivePrompt, createExtractionRun, completeExtractionRun } from '../db.js';

export async function generateQbrNarrative({
  project, setup, dataResult, userId, priorQuarter = null,
}) {
  const prompt = await getActivePrompt('QBR', 'narrative');
  if (!prompt?.body) {
    throw new Error('No active QBR narrative prompt found. An admin needs to seed one in the Prompts tab.');
  }

  // Build the inputs the template references. The template uses
  // METRICS_JSON / GRADES_JSON / PRIOR_QUARTER_JSON as JSON-stringified
  // blocks so Claude sees the full structure.
  const metricsForPrompt = buildMetricsPayload(dataResult);
  const gradesForPrompt  = buildGradesPayload(dataResult);
  // Top equipment list (slide 14). Claude reads { name, locationName,
  // count } per row and generates a recommended_action for each.
  const assetHealthList = (dataResult?.overall?.topEquipment || dataResult?.topEquipment || [])
    .map(({ name, locationName, count }) => ({ name, locationName, count }));

  const systemPrompt = buildSystemPrompt({
    body: prompt.body,
    vars: {
      CUSTOMER_NAME:       project?.name || 'Customer',
      QUARTER_LABEL:       setup?.quarterLabel || '',
      METRICS_JSON:        JSON.stringify(metricsForPrompt, null, 2),
      GRADES_JSON:         JSON.stringify(gradesForPrompt, null, 2),
      PRIOR_QUARTER_JSON:  priorQuarter ? JSON.stringify(priorQuarter, null, 2) : 'null',
      ASSET_HEALTH_JSON:   JSON.stringify(assetHealthList, null, 2),
    },
    examples: [],
  });

  // Log the run before firing so failures are still visible to admins.
  const run = await createExtractionRun({
    projectId:      project?.id,
    userId,
    migrationType:  'QBR',
    stage:          'narrative',
    storageKey:     null,
    sourceFilename: setup?.quarterLabel || 'qbr',
    pageCount:      0,
    promptId:       prompt.id,
    promptVersion:  prompt.version,
  }).catch(() => null);

  try {
    const response = await claudeFetch({
      system: systemPrompt,
      max_tokens: 2500,
      messages: [{
        role: 'user',
        content: 'Generate the QBR narrative JSON now.',
      }],
    });

    const text = parseClaudeText(response);
    const narrative = safeJsonParse(text);
    if (!narrative) {
      const e = new Error(
        'Claude returned content that couldn\'t be parsed as JSON. ' +
        'Most often this is the model adding prose around the JSON, or hitting the token cap mid-output. ' +
        'Try Regenerate; if it keeps failing, tweak the prompt in the admin QBR tab.'
      );
      e.rawText = text;
      throw e;
    }

    // Best-effort run logging — never block the user on the audit write.
    if (run?.id) {
      const usage = extractUsage(response);
      completeExtractionRun(run.id, {
        status: 'complete',
        resultJson: narrative,
        inputTokens:  usage.inputTokens,
        outputTokens: usage.outputTokens,
        estimatedCostUsd: usage.costUsd,
      }).catch(() => {});
    }
    return { narrative, runId: run?.id || null };
  } catch (e) {
    if (run?.id) {
      completeExtractionRun(run.id, {
        status: 'error',
        error: e.message,
      }).catch(() => {});
    }
    throw e;
  }
}

// Shapes dataResult.prior (fetchPriorQuarterSummary's output) into the
// PRIOR_QUARTER_JSON payload the prompt documents. Null when no prior
// data was fetched — the prompt handles that ('Empty string if
// PRIOR_QUARTER_JSON is null' for the wr_volume_trend caption).
// Adoption is absent by design: it can't be computed for a historical
// window (see fetchPriorQuarterSummary), so we don't tempt Claude with
// a fabricated comparison.
export function buildPriorQuarterPayload(prior) {
  if (!prior || (!prior.response && prior.woCount == null)) return null;
  return {
    quarter_label: prior.quarterLabel || null,
    wo_count:      prior.woCount ?? null,
    response_time: prior.response ? {
      responseDays:       prior.response.responseDays ?? null,
      responseMeanDays:   prior.response.responseMeanDays ?? null,
      resolutionDays:     prior.response.resolutionDays ?? null,
      resolutionMeanDays: prior.response.resolutionMeanDays ?? null,
    } : null,
  };
}

// Pulls just the numbers Claude needs — overall metric values plus the
// tenant-global adoption. Per-module breakdown isn't passed yet because
// the seeded prompt only references the overall shape. When the prompt
// grows to address per-module storylines, expand this payload.
function buildMetricsPayload(dataResult) {
  const overall = dataResult?.overall?.values || dataResult?.values || {};
  const tenant  = dataResult?.tenant?.values  || {};
  return {
    completion_rate:     overall.completion_rate     ?? null,
    response_time:       overall.response_time       ?? null,
    planned_vs_reactive: overall.planned_vs_reactive ?? null,
    pm_compliance:       overall.pm_compliance       ?? null,
    backlog_aging:       overall.backlog_aging       ?? null,
    adoption:            tenant.adoption || overall.adoption || null,
    adoption_power:      overall.adoption_power || null,
  };
}

function buildGradesPayload(dataResult) {
  const overall = dataResult?.overall?.grades || dataResult?.grades || {};
  const tenant  = dataResult?.tenant?.grades  || {};
  return {
    completion_rate:     overall.completion_rate     ?? null,
    response_time:       overall.response_time       ?? null,
    planned_vs_reactive: overall.planned_vs_reactive ?? null,
    pm_compliance:       overall.pm_compliance       ?? null,
    backlog_aging:       overall.backlog_aging       ?? null,
    adoption:            tenant.adoption || overall.adoption || null,
    adoption_power:      overall.adoption_power || null,
  };
}

// Claude reliably-ish returns plain JSON but sometimes adds preamble
// ("Here's the JSON:"), a code fence, or a trailing comma. We try
// progressively-tolerant parses before giving up.
function safeJsonParse(text) {
  if (!text) return null;
  const attempts = [];
  attempts.push(text);

  // Strip Markdown code fences if any survived parseClaudeText.
  const fenced = text.replace(/```(?:json|javascript|js)?\s*([\s\S]*?)```/g, '$1');
  if (fenced !== text) attempts.push(fenced);

  // Slice to the outermost {...} block.
  const start = text.indexOf('{');
  const end   = text.lastIndexOf('}');
  if (start !== -1 && end > start) attempts.push(text.slice(start, end + 1));

  // Same slice, with trailing commas before `}` or `]` removed — a common
  // Claude tic that's invalid JSON.
  for (const a of [...attempts]) {
    const cleaned = a.replace(/,(\s*[}\]])/g, '$1');
    if (cleaned !== a) attempts.push(cleaned);
  }

  for (const t of attempts) {
    try { return JSON.parse(t); } catch {}
  }
  return null;
}

// Empty-narrative scaffold used by the Preview step when nothing has
// been generated yet (or when the FMXer wants to skip the AI and just
// write from scratch).
export function emptyNarrative() {
  return {
    exec_summary:       { wins: ['', '', ''], risks: ['', ''] },
    metric_callouts:    {
      completion_rate: '', response_time: '', planned_vs_reactive: '',
      pm_compliance: '',   backlog_aging: '', adoption: '',
    },
    // Per-slide story sentences (slides 7, 10, 11). Distinct from the
    // metric_callouts because they sit in specific spots on those
    // slides ('Driven by ...', 'First quarter above ...', 'RECOMMEND').
    slide_captions:     {
      maintenance_activity: '',
      planned_vs_reactive:  '',
      pm_recommend:         '',
      wr_volume_trend:      '',
      top_building_callout: '',
      response_benchmark:   '',
      scheduling_top_use:   '',
      invoicing_approval_time: '',
    },
    worth_exploring:    [
      { title: '', desc: '' },
      { title: '', desc: '' },
      { title: '', desc: '' },
      { title: '', desc: '' },
    ],
    customer_win:       '',
    // Per-equipment recommended-action list for slide 14. Shape:
    // [{ name, locationName, count, recommended_action }].
    asset_health:       [],
    mutual_action_plan: [],
    // Roadmap items now carry a description in addition to the title.
    roadmap_teaser:     [
      { title: '', description: '' },
      { title: '', description: '' },
      { title: '', description: '' },
    ],
  };
}
